import { EventEmitter } from '../../core/Event';
import type { SlotGameEvent } from './SlotGameEvents';

export interface SlotGameModelConfig {
  startingBalance: number;
  betAmounts: readonly number[];
  initialBet: number;
  /** Cap on consecutive gambles per win — auto-collect at this many. */
  maxGambleStreak: number;
}

/**
 * Authoritative state for a slot game session: balance, current bet, the
 * "pending winnings" bank between a winning spin and its collection, the
 * gamble streak counter, and the per-reel held flags.
 *
 * <p>External access is via getters and {@link events}; mutations happen
 * through methods on the model that emit events. In Phase B.2 these methods
 * become the targets of Logic command classes (one Logic per intent).
 *
 * <p>Why a model at all (vs. fields on the scene): scaling MMS-class scope
 * — ~50 logic flows touching ~30 state slices — is intractable when state
 * lives in the rendering layer. The model lets Logic + Listeners + Animations
 * be developed and tested in isolation, and lets multiple components
 * (HUD, machine, gamble overlay, paytable) react to the same change without
 * wiring callback chains.
 */
export class SlotGameModel {
  public readonly events = new EventEmitter<SlotGameEvent>();

  private readonly betAmounts: readonly number[];
  public readonly maxGambleStreak: number;

  private _balance: number;
  private _currentBet: number;
  private _spinning = false;
  private _pendingWinnings = 0;
  private _gambleStreak = 0;
  private _freeSpinsRemaining = 0;
  private _heldReels = new Set<number>();

  constructor(config: SlotGameModelConfig) {
    this._balance = config.startingBalance;
    this.betAmounts = config.betAmounts;
    this._currentBet = config.initialBet;
    this.maxGambleStreak = config.maxGambleStreak;
  }

  // -------- balance --------

  get balance(): number {
    return this._balance;
  }

  setBalance(after: number, reason: 'spin-bet' | 'collect' | 'session-grant' | 'admin'): void {
    if (after === this._balance) return;
    const before = this._balance;
    this._balance = after;
    this.events.emit({ kind: 'balance-changed', before, after, reason });
  }

  /** Convenience: spend coins from balance. Emits balance-changed; returns false
   * if the player can't afford. The host shouldn't trust the returned bool —
   * Logic checks affordability separately and emits {@code not-enough-coins}. */
  debit(amount: number, reason: 'spin-bet' | 'collect' | 'session-grant' | 'admin' = 'spin-bet'): boolean {
    if (amount <= 0) return true;
    if (this._balance < amount) {
      this.events.emit({ kind: 'not-enough-coins', required: amount, have: this._balance });
      return false;
    }
    this.setBalance(this._balance - amount, reason);
    return true;
  }

  credit(amount: number, reason: 'spin-bet' | 'collect' | 'session-grant' | 'admin' = 'collect'): void {
    if (amount === 0) return;
    this.setBalance(this._balance + amount, reason);
  }

  // -------- bet --------

  get currentBet(): number {
    return this._currentBet;
  }

  get availableBetAmounts(): readonly number[] {
    return this.betAmounts;
  }

  setBet(after: number): void {
    if (after === this._currentBet) return;
    if (!this.betAmounts.includes(after)) {
      throw new Error(`Bet ${after} not in available amounts ${this.betAmounts.join(', ')}`);
    }
    const before = this._currentBet;
    this._currentBet = after;
    this.events.emit({ kind: 'bet-changed', before, after });
  }

  /** Snap the bet down to the highest amount the player can still afford. No-op
   * if {@link currentBet} is already affordable. */
  clampBetToAffordable(): void {
    if (this._currentBet <= this._balance) return;
    let bestAffordable = this.betAmounts[0]!;
    for (const amount of this.betAmounts) {
      if (amount <= this._balance) bestAffordable = amount;
    }
    this.setBet(bestAffordable);
  }

  // -------- spin / pending winnings / gamble streak --------

  get spinning(): boolean {
    return this._spinning;
  }

  /** Mark spinning true and emit spin-started. Does not deduct the bet — Logic
   * (Phase B.2) decides whether to debit before this. */
  markSpinStarted(): void {
    this._spinning = true;
    this.events.emit({ kind: 'spin-started', bet: this._currentBet });
  }

  /** Mark spinning false. Emitting spin-landed is the caller's responsibility
   * since they're the ones who computed the outcome + settlement. */
  markSpinLanded(): void {
    this._spinning = false;
  }

  get pendingWinnings(): number {
    return this._pendingWinnings;
  }

  setPendingWinnings(value: number): void {
    if (value === this._pendingWinnings) return;
    const before = this._pendingWinnings;
    this._pendingWinnings = value;
    this.events.emit({ kind: 'pending-winnings-changed', before, after: value });
  }

  get gambleStreak(): number {
    return this._gambleStreak;
  }

  resetGambleStreak(): void {
    if (this._gambleStreak === 0) return;
    const before = this._gambleStreak;
    this._gambleStreak = 0;
    this.events.emit({ kind: 'gamble-streak-changed', before, after: 0 });
  }

  incrementGambleStreak(): number {
    const before = this._gambleStreak;
    this._gambleStreak += 1;
    this.events.emit({ kind: 'gamble-streak-changed', before, after: this._gambleStreak });
    return this._gambleStreak;
  }

  canGamble(): boolean {
    return this._gambleStreak < this.maxGambleStreak && this._pendingWinnings > 0;
  }

  // -------- free-spin bonus --------

  get freeSpinsRemaining(): number {
    return this._freeSpinsRemaining;
  }

  /** True iff the next spin should consume a free spin instead of debiting. */
  hasFreeSpinAvailable(): boolean {
    return this._freeSpinsRemaining > 0;
  }

  /** Award {@code count} free spins. Adds to any existing balance — back-to-back
   * triggers stack rather than reset, matching cs-core's MMS behaviour. */
  grantFreeSpins(count: number, source: 'scatter' = 'scatter'): void {
    if (count <= 0) return;
    const before = this._freeSpinsRemaining;
    this._freeSpinsRemaining += count;
    this.events.emit({ kind: 'free-spins-granted', count, source });
    this.events.emit({
      kind: 'free-spins-changed',
      before,
      after: this._freeSpinsRemaining,
    });
  }

  /** Consume one free spin if available. Returns true on success, false if
   * the counter was already 0 — Logic treats false as "needs to debit bet". */
  consumeFreeSpin(): boolean {
    if (this._freeSpinsRemaining <= 0) return false;
    const before = this._freeSpinsRemaining;
    this._freeSpinsRemaining -= 1;
    this.events.emit({
      kind: 'free-spins-changed',
      before,
      after: this._freeSpinsRemaining,
    });
    return true;
  }

  /**
   * Phase D.2 — server-driven sync of the free-spin counter. Server is
   * authoritative; the client mirrors {@code freeSpinsRemaining} from
   * the spin response on every spin. {@link grantFreeSpins} +
   * {@link consumeFreeSpin} are still valid for offline / local-only
   * play; this is the explicit "set to server's value" door.
   *
   * <p>Emits {@code free-spins-changed} on a real delta so HUD chips
   * stay in sync without each scene re-running the math. Does not emit
   * {@code free-spins-granted} — that event is for "this spin triggered
   * the bonus" and the scene fires it explicitly when the response's
   * {@code freeSpinsGranted} is non-zero.
   */
  setFreeSpinsRemaining(after: number): void {
    if (after < 0) {
      throw new Error(`free spins must be non-negative, got ${after}`);
    }
    if (after === this._freeSpinsRemaining) return;
    const before = this._freeSpinsRemaining;
    this._freeSpinsRemaining = after;
    this.events.emit({
      kind: 'free-spins-changed',
      before,
      after,
    });
  }

  // -------- holds --------

  isReelHeld(reelIndex: number): boolean {
    return this._heldReels.has(reelIndex);
  }

  setReelHeld(reelIndex: number, held: boolean): void {
    const wasHeld = this._heldReels.has(reelIndex);
    if (wasHeld === held) return;
    if (held) {
      this._heldReels.add(reelIndex);
    } else {
      this._heldReels.delete(reelIndex);
    }
    this.events.emit({ kind: 'reel-hold-toggled', reelIndex, held });
  }

  clearAllHolds(): void {
    if (this._heldReels.size === 0) return;
    const indices = [...this._heldReels];
    this._heldReels.clear();
    for (const i of indices) {
      this.events.emit({ kind: 'reel-hold-toggled', reelIndex: i, held: false });
    }
  }

  // -------- lifecycle --------

  /** Drop every event listener. Call when the host scene destroys. */
  dispose(): void {
    this.events.clear();
  }
}
